'use client';

/**
 * Pricing Proposal Form Component
 *
 * Form for creating and editing pricing proposals with milestone support.
 * Supports different pricing models: per_service, milestone_based, hourly, package.
 *
 * Integrates with Migration 124: Pricing & Billing Proposals
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DollarSign,
  Plus,
  Trash2,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calculator,
} from 'lucide-react';
import {
  pricingProposalsAPI,
  PricingModel,
  PricingMilestone,
  PricingAdjustment,
  CreateProposalRequest,
  formatCurrency,
  calculateMilestoneTotal,
  validateMilestonePercentages,
  getPricingModelLabel,
} from '@/services/api/pricingProposals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PricingProposalFormProps {
  referralId: number;
  providerType: string;
  providerId: number | string | undefined;
  providerName?: string;
  serviceTypeCode?: string;
  urgencyLevel?: string;
  onSubmit?: (proposalId: number) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateProposalRequest>;
  isCounterProposal?: boolean;
  originalAmount?: number;
  className?: string;
}

interface MilestoneFormData {
  id: string; // Temp ID for tracking
  order: number;
  name: string;
  description: string;
  percentage: number;
  amount: number;
  criteria: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PricingProposalForm({
  referralId,
  providerType,
  providerId,
  providerName,
  serviceTypeCode,
  urgencyLevel = 'routine',
  onSubmit,
  onCancel,
  initialData,
  isCounterProposal = false,
  originalAmount,
  className = '',
}: PricingProposalFormProps) {
  const { t } = useAppTranslation();
  // Form state
  const [pricingModel, setPricingModel] = useState<PricingModel>(
    initialData?.pricing_model || 'milestone_based'
  );
  const [baseRate, setBaseRate] = useState<number>(initialData?.base_rate || 0);
  const [hourlyRate, setHourlyRate] = useState<number>(initialData?.hourly_rate || 0);
  const [estimatedHours, setEstimatedHours] = useState<number>(initialData?.estimated_hours || 0);
  const [milestones, setMilestones] = useState<MilestoneFormData[]>([]);
  const [adjustments, setAdjustments] = useState<PricingAdjustment[]>(
    initialData?.adjustments || []
  );
  const [counterReason, setCounterReason] = useState('');
  const [validDays, setValidDays] = useState(7);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load rate card on mount
  useEffect(() => {
    if (serviceTypeCode && !initialData?.base_rate) {
      loadRateCard();
    }
  }, [serviceTypeCode]);

  // Initialize milestones if not provided
  useEffect(() => {
    if (initialData?.milestones && initialData.milestones.length > 0) {
      setMilestones(
        initialData.milestones.map((m, i) => ({
          id: `milestone-${i}`,
          order: m.order,
          name: m.name,
          description: m.description || '',
          percentage: m.percentage,
          amount: m.amount,
          criteria: m.criteria || '',
        }))
      );
    } else {
      // Default milestones
      setMilestones(getDefaultMilestones());
    }
  }, []);

  const loadRateCard = async () => {
    if (!serviceTypeCode) return;

    setLoadingRates(true);
    try {
      const rateCard = await pricingProposalsAPI.getRateCardForService(
        providerType,
        providerId,
        serviceTypeCode
      );

      if (rateCard) {
        // Set rate based on urgency
        let rate = rateCard.standard_rate || 0;
        if (urgencyLevel === 'urgent' && rateCard.urgent_rate) {
          rate = rateCard.urgent_rate;
        } else if (urgencyLevel === 'emergent' && rateCard.emergent_rate) {
          rate = rateCard.emergent_rate;
        }

        setBaseRate(rate);
        setPricingModel(rateCard.preferred_pricing_model);

        // Update milestone amounts
        updateMilestoneAmounts(rate);
      }
    } catch (err) {
      console.error('Failed to load rate card:', err);
      // Use defaults
    } finally {
      setLoadingRates(false);
    }
  };

  const getDefaultMilestones = (): MilestoneFormData[] => [
    {
      id: 'milestone-1',
      order: 1,
      name: 'Initial Assessment',
      description: 'Initial member assessment completed and documented',
      percentage: 25,
      amount: baseRate * 0.25,
      criteria: 'Assessment documentation submitted',
    },
    {
      id: 'milestone-2',
      order: 2,
      name: 'Service Delivery',
      description: 'Core service deliverables completed',
      percentage: 50,
      amount: baseRate * 0.5,
      criteria: 'Service delivery confirmed',
    },
    {
      id: 'milestone-3',
      order: 3,
      name: 'Completion & Documentation',
      description: 'Service completed with all documentation',
      percentage: 25,
      amount: baseRate * 0.25,
      criteria: 'Final documentation submitted',
    },
  ];

  const updateMilestoneAmounts = (total: number) => {
    setMilestones((prev) =>
      prev.map((m) => ({
        ...m,
        amount: Math.round((total * m.percentage) / 100 * 100) / 100,
      }))
    );
  };

  const handleBaseRateChange = (value: number) => {
    setBaseRate(value);
    if (pricingModel === 'milestone_based') {
      updateMilestoneAmounts(value);
    }
  };

  const addMilestone = () => {
    const newOrder = milestones.length + 1;
    setMilestones([
      ...milestones,
      {
        id: `milestone-${Date.now()}`,
        order: newOrder,
        name: `Milestone ${newOrder}`,
        description: '',
        percentage: 0,
        amount: 0,
        criteria: '',
      },
    ]);
  };

  const removeMilestone = (id: string) => {
    setMilestones((prev) =>
      prev
        .filter((m) => m.id !== id)
        .map((m, i) => ({ ...m, order: i + 1 }))
    );
  };

  const updateMilestone = (id: string, updates: Partial<MilestoneFormData>) => {
    setMilestones((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;

        const updated = { ...m, ...updates };

        // If percentage changed, recalculate amount
        if (updates.percentage !== undefined) {
          updated.amount = Math.round((baseRate * updates.percentage) / 100 * 100) / 100;
        }

        // If amount changed, recalculate percentage
        if (updates.amount !== undefined && baseRate > 0) {
          updated.percentage = Math.round((updates.amount / baseRate) * 100 * 100) / 100;
        }

        return updated;
      })
    );
  };

  const addAdjustment = () => {
    setAdjustments([
      ...adjustments,
      { type: 'other', description: '', amount: 0 },
    ]);
  };

  const removeAdjustment = (index: number) => {
    setAdjustments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAdjustment = (index: number, updates: Partial<PricingAdjustment>) => {
    setAdjustments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...updates } : a))
    );
  };

  const calculateTotal = (): number => {
    let total = 0;

    if (pricingModel === 'milestone_based') {
      total = calculateMilestoneTotal(milestones as any);
    } else if (pricingModel === 'hourly') {
      total = hourlyRate * estimatedHours;
    } else {
      total = baseRate;
    }

    // Add adjustments
    total += adjustments.reduce((sum, a) => sum + (a.amount || 0), 0);

    return total;
  };

  const validate = (): string[] => {
    const errors: string[] = [];

    if (pricingModel === 'milestone_based') {
      if (milestones.length === 0) {
        errors.push('At least one milestone is required');
      }

      if (!validateMilestonePercentages(milestones as any)) {
        errors.push('Milestone percentages must total 100%');
      }

      milestones.forEach((m, i) => {
        if (!m.name.trim()) {
          errors.push(`Milestone ${i + 1} requires a name`);
        }
      });
    }

    if (pricingModel === 'hourly' && hourlyRate <= 0) {
      errors.push('Hourly rate must be greater than 0');
    }

    if (pricingModel === 'per_service' && baseRate <= 0) {
      errors.push('Base rate must be greater than 0');
    }

    if (isCounterProposal && !counterReason.trim()) {
      errors.push('Please provide a reason for the counter proposal');
    }

    return errors;
  };

  const handleSubmit = async (submitProposal = false) => {
    const errors = validate();
    setValidationErrors(errors);

    if (errors.length > 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: CreateProposalRequest = {
        service_referral_id: referralId,
        provider_type: providerType,
        provider_id: providerId,
        pricing_model: pricingModel,
        base_rate: baseRate,
        valid_days: validDays,
      };

      if (pricingModel === 'milestone_based') {
        data.milestones = milestones.map((m) => ({
          order: m.order,
          name: m.name,
          description: m.description,
          percentage: m.percentage,
          amount: m.amount,
          criteria: m.criteria,
        }));
      }

      if (pricingModel === 'hourly') {
        data.hourly_rate = hourlyRate;
        data.estimated_hours = estimatedHours;
      }

      if (adjustments.length > 0) {
        data.adjustments = adjustments.filter((a) => a.description && a.amount !== 0);
      }

      const proposal = await pricingProposalsAPI.createProposal(data);

      if (submitProposal) {
        await pricingProposalsAPI.submitProposal(proposal.id);
      }

      onSubmit?.(proposal.id);
    } catch (err: any) {
      setError(err.message || 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  const totalPercentage = milestones.reduce((sum, m) => sum + m.percentage, 0);
  const totalAmount = calculateTotal();

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {isCounterProposal
                ? t('serviceReferrals.proposal.submitCounterProposal', 'Submit Counter Proposal')
                : t('serviceReferrals.proposal.createPricingProposal', 'Create Pricing Proposal')}
            </CardTitle>
            <CardDescription>
              {providerName && `${t('serviceReferrals.proposal.for', 'For')} ${providerName}`}
              {originalAmount && ` • ${t('serviceReferrals.proposal.original', 'Original')}: ${formatCurrency(originalAmount)}`}
            </CardDescription>
          </div>
          {loadingRates && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('serviceReferrals.proposal.loadingRates', 'Loading rates...')}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Pricing Model Selection */}
        <div className="space-y-2">
          <Label>{t('serviceReferrals.proposal.pricingModel', 'Pricing Model')}</Label>
          <Select value={pricingModel} onValueChange={(v) => setPricingModel(v as PricingModel)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="milestone_based">{t('serviceReferrals.proposal.milestoneBased', 'Milestone Based')}</SelectItem>
              <SelectItem value="per_service">{t('serviceReferrals.proposal.perService', 'Per Service (Fixed)')}</SelectItem>
              <SelectItem value="hourly">{t('serviceReferrals.proposal.hourly', 'Hourly')}</SelectItem>
              <SelectItem value="package">{t('serviceReferrals.proposal.package', 'Package')}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            {pricingModel === 'milestone_based' && t('serviceReferrals.proposal.milestoneDesc', 'Payment released as milestones are completed and verified')}
            {pricingModel === 'per_service' && t('serviceReferrals.proposal.perServiceDesc', 'Single payment upon service completion')}
            {pricingModel === 'hourly' && t('serviceReferrals.proposal.hourlyDesc', 'Payment based on hours worked')}
            {pricingModel === 'package' && t('serviceReferrals.proposal.packageDesc', 'Bundled services at a package rate')}
          </p>
        </div>

        {/* Base Rate (for per_service and milestone_based) */}
        {(pricingModel === 'per_service' || pricingModel === 'milestone_based' || pricingModel === 'package') && (
          <div className="space-y-2">
            <Label>{t('serviceReferrals.proposal.totalServiceAmount', 'Total Service Amount')}</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                min={0}
                step={0.01}
                value={baseRate}
                onChange={(e) => handleBaseRateChange(parseFloat(e.target.value) || 0)}
                className="pl-9"
                placeholder="0.00"
              />
            </div>
          </div>
        )}

        {/* Hourly Pricing */}
        {pricingModel === 'hourly' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('serviceReferrals.proposal.hourlyRate', 'Hourly Rate')}</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                  className="pl-9"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('serviceReferrals.proposal.estimatedHours', 'Estimated Hours')}</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(parseFloat(e.target.value) || 0)}
                placeholder="0"
              />
            </div>
          </div>
        )}

        {/* Milestones */}
        {pricingModel === 'milestone_based' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{t('serviceReferrals.proposal.paymentMilestones', 'Payment Milestones')}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
                <Plus className="h-4 w-4 mr-1" />
                {t('serviceReferrals.proposal.addMilestone', 'Add Milestone')}
              </Button>
            </div>

            <div className="space-y-3">
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.id}
                  className="p-4 border rounded-lg bg-gray-50 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium">{t('serviceReferrals.proposal.milestone', 'Milestone')} {index + 1}</span>
                    </div>
                    {milestones.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMilestone(milestone.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Input
                        value={milestone.name}
                        onChange={(e) => updateMilestone(milestone.id, { name: e.target.value })}
                        placeholder={t('serviceReferrals.proposal.milestoneName', 'Milestone name')}
                      />
                    </div>
                    <div>
                      <div className="relative">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={milestone.percentage}
                          onChange={(e) =>
                            updateMilestone(milestone.id, {
                              percentage: parseFloat(e.target.value) || 0,
                            })
                          }
                          placeholder={t('serviceReferrals.proposal.percentage', 'Percentage')}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          %
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          value={milestone.amount}
                          onChange={(e) =>
                            updateMilestone(milestone.id, {
                              amount: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="pl-9"
                          placeholder={t('serviceReferrals.proposal.amount', 'Amount')}
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={milestone.criteria}
                        onChange={(e) =>
                          updateMilestone(milestone.id, { criteria: e.target.value })
                        }
                        placeholder={t('serviceReferrals.proposal.completionCriteria', 'Completion criteria')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Milestone Totals */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
              <span className="text-sm font-medium">{t('serviceReferrals.proposal.total', 'Total')}</span>
              <div className="flex items-center gap-4">
                <span
                  className={`text-sm ${
                    Math.abs(totalPercentage - 100) < 0.01
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {totalPercentage.toFixed(1)}%
                </span>
                <span className="text-sm font-semibold">
                  {formatCurrency(calculateMilestoneTotal(milestones as any))}
                </span>
              </div>
            </div>

            {Math.abs(totalPercentage - 100) >= 0.01 && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {t('serviceReferrals.proposal.milestonePercentageError', 'Milestone percentages must total 100%')}
              </div>
            )}
          </div>
        )}

        {/* Adjustments */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t('serviceReferrals.proposal.adjustments', 'Adjustments')}</Label>
            <Button type="button" variant="outline" size="sm" onClick={addAdjustment}>
              <Plus className="h-4 w-4 mr-1" />
              {t('serviceReferrals.proposal.addAdjustment', 'Add Adjustment')}
            </Button>
          </div>

          {adjustments.length > 0 && (
            <div className="space-y-2">
              {adjustments.map((adjustment, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Select
                    value={adjustment.type}
                    onValueChange={(v) => updateAdjustment(index, { type: v })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgency">{t('serviceReferrals.proposal.adjustmentUrgency', 'Urgency')}</SelectItem>
                      <SelectItem value="distance">{t('serviceReferrals.proposal.adjustmentDistance', 'Distance')}</SelectItem>
                      <SelectItem value="discount">{t('serviceReferrals.proposal.adjustmentDiscount', 'Discount')}</SelectItem>
                      <SelectItem value="other">{t('serviceReferrals.proposal.adjustmentOther', 'Other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={adjustment.description}
                    onChange={(e) => updateAdjustment(index, { description: e.target.value })}
                    placeholder={t('serviceReferrals.proposal.description', 'Description')}
                    className="flex-1"
                  />
                  <div className="relative w-28">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      step={0.01}
                      value={adjustment.amount}
                      onChange={(e) =>
                        updateAdjustment(index, { amount: parseFloat(e.target.value) || 0 })
                      }
                      className="pl-9"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAdjustment(index)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Counter Reason (for counter proposals) */}
        {isCounterProposal && (
          <div className="space-y-2">
            <Label>{t('serviceReferrals.proposal.reasonForCounterProposal', 'Reason for Counter Proposal')}</Label>
            <Textarea
              value={counterReason}
              onChange={(e) => setCounterReason(e.target.value)}
              placeholder={t('serviceReferrals.proposal.explainDifferentTerms', 'Explain why you are proposing different terms...')}
              rows={3}
            />
          </div>
        )}

        {/* Validity Period */}
        <div className="space-y-2">
          <Label>{t('serviceReferrals.proposal.validFor', 'Valid For')}</Label>
          <Select value={validDays.toString()} onValueChange={(v) => setValidDays(parseInt(v))}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">{t('serviceReferrals.proposal.days3', '3 days')}</SelectItem>
              <SelectItem value="7">{t('serviceReferrals.proposal.days7', '7 days')}</SelectItem>
              <SelectItem value="14">{t('serviceReferrals.proposal.days14', '14 days')}</SelectItem>
              <SelectItem value="30">{t('serviceReferrals.proposal.days30', '30 days')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Total Summary */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-900">{t('serviceReferrals.proposal.totalAmount', 'Total Amount')}</span>
            </div>
            <span className="text-2xl font-bold text-blue-900">{formatCurrency(totalAmount)}</span>
          </div>
          {isCounterProposal && originalAmount && (
            <div className="mt-2 text-sm text-blue-700">
              {totalAmount > originalAmount
                ? `+${formatCurrency(totalAmount - originalAmount)} ${t('serviceReferrals.proposal.fromOriginal', 'from original')}`
                : totalAmount < originalAmount
                ? `${formatCurrency(totalAmount - originalAmount)} ${t('serviceReferrals.proposal.fromOriginal', 'from original')}`
                : t('serviceReferrals.proposal.sameAsOriginal', 'Same as original')}
            </div>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
            {validationErrors.map((err, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4" />
                {err}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between gap-3 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit(false)}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('serviceReferrals.proposal.saveDraft', 'Save Draft')}
          </Button>
          <Button type="button" onClick={() => handleSubmit(true)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isCounterProposal
              ? t('serviceReferrals.proposal.submitCounter', 'Submit Counter')
              : t('serviceReferrals.proposal.submitProposal', 'Submit Proposal')}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

export default PricingProposalForm;
